import type { ReactNode } from "react";
import { BrandTitle } from "../../app/BrandTitle";
import { LanguageToggle } from "../../i18n/LanguageToggle";
import { useI18n } from "../../i18n/localization";

// Minimal chrome for the public root and guest link: brand + language switch only — no
// Home/Meetings/Admin navigation and no system content beyond the current step.
export function PublicShell({ children }: { children: ReactNode }) {
  const { dir } = useI18n();
  return (
    <div
      className="vc-shell vc-shell--public"
      dir={dir}
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#eef2f7", fontFamily: "Segoe UI, Arial, sans-serif" }}
    >
      <header style={{ background: "#14305f", color: "#fff", padding: "10px 18px", display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" }}>
        <BrandTitle />
        <span style={{ marginInlineStart: "auto", flex: "0 0 auto" }}><LanguageToggle /></span>
      </header>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>{children}</div>
    </div>
  );
}

/** Centered step card used by every non-media step. */
export function StepCard({ title, labelledBy, children }: { title: string; labelledBy: string; children: ReactNode }) {
  return (
    <main style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px" }}>
      <section aria-labelledby={labelledBy}
        style={{ width: "100%", maxWidth: 440, background: "#fff", border: "1px solid #d6deea", borderRadius: 12, padding: 24, boxShadow: "0 6px 24px rgba(20,48,95,.08)", boxSizing: "border-box" }}>
        <h1 id={labelledBy} style={{ color: "#14305f", fontSize: 20, margin: "0 0 10px" }}>{title}</h1>
        {children}
      </section>
    </main>
  );
}

export function FieldError({ message }: { message: string | null }) {
  return (
    <div aria-live="assertive" style={{ minHeight: 18, margin: "6px 0 10px" }}>
      {message && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{message}</div>}
    </div>
  );
}

export function PrimaryButton({ busy, children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }) {
  return (
    <button {...rest} disabled={busy || rest.disabled} aria-busy={busy}
      style={{ width: "100%", background: busy ? "#94a3b8" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 15, cursor: busy ? "default" : "pointer" }}>
      {children}
    </button>
  );
}

export function LinkButton({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" {...rest}
      style={{ background: "none", border: "none", color: "#1d4ed8", padding: "8px 0", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
      {children}
    </button>
  );
}

export const textInput: React.CSSProperties = {
  display: "block", width: "100%", marginTop: 6, padding: "10px 12px", fontSize: 16,
  border: "1px solid #c3cfe2", borderRadius: 8, boxSizing: "border-box"
};
export const label: React.CSSProperties = { display: "block", color: "#14305f", fontSize: 14 };
export const muted: React.CSSProperties = { color: "#5b6b82", fontSize: 14, margin: "0 0 16px", lineHeight: 1.5 };
